import { logger } from '../utils/logger';
import externalInvitationService from './supabaseExternalInvitationService';

/**
 * RepConnect - External Provider Invitation System
 *
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 *
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

// External Provider Invitation Management System
export const ExternalProviderInvitation = {

  /**
   * Create an external provider invitation for unregistered physicians
   * UPDATED: Now saves to Supabase database instead of localStorage
   * @param {Object} invitationData - Invitation details
   * @param {Object} invitationData.provider - Provider information from NPI search
   * @param {Object} invitationData.salesRep - Sales representative information
   * @param {Object} invitationData.meetingDetails - Meeting details
   * @param {string} invitationData.providerEmail - Manually entered email
   * @returns {Promise<Object>} Created invitation object from database
   */
  async createInvitation(invitationData) {
    try {
      const { provider, salesRep, meetingDetails, providerEmail } = invitationData;

      // Generate unique token for confirmation links
      const token = this.generateInvitationToken();

      // Prepare invitation object
      const invitation = {
        token,

        // Provider information
        providerNPI: provider.npi,
        providerName: provider.name,
        providerEmail: providerEmail,
        providerSpecialty: provider.specialty,
        providerHospital: provider.hospital,
        providerLocation: provider.location || null,

        // Sales rep information
        salesRepId: salesRep.id,
        salesRepName: `${salesRep.first_name || ''} ${salesRep.last_name || ''}`.trim() || salesRep.fullName || salesRep.name,
        salesRepEmail: salesRep.email,
        salesRepCompany: salesRep.company || 'Pharmaceutical Company',
        salesRepPhone: salesRep.phone || null,

        // Meeting details
        meetingDate: meetingDetails.date,
        meetingTime: meetingDetails.time,
        restaurant: meetingDetails.restaurant?.name || 'Restaurant TBD',
        restaurantAddress: meetingDetails.restaurant?.location?.display_address?.join(', ') || meetingDetails.restaurant?.address || '',
        restaurantYelpId: meetingDetails.restaurant?.id || null,
        notes: meetingDetails.notes || ''
      };

      // Generate email content BEFORE saving (needed for email)
      const emailContent = this.generateInvitationEmail({
        ...invitation,
        // Add fields needed for email generation
        meetingTime: meetingDetails.time
      });

      // Save to Supabase database
      const savedInvitation = await externalInvitationService.createInvitation(invitation);

      if (!savedInvitation) {
        throw new Error('Failed to save invitation to database');
      }

      // Add email content to returned object (not stored in DB)
      savedInvitation.emailContent = emailContent;

      logger.debug('✅ External provider invitation created in database:', savedInvitation);
      return savedInvitation;

    } catch (error) {
      logger.error('Error creating external provider invitation', { error: error.message });
      throw new Error(`Failed to create provider invitation: ${error.message}`);
    }
  },
  
  /**
   * Generate secure invitation token for email links
   * @returns {string} Secure token
   */
  generateInvitationToken() {
    // Generate a secure token
    return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}_${Math.random().toString(36).substring(2, 15)}`;
  },

  /**
   * Get invitation by token (from Supabase)
   * @param {string} token - Invitation token
   * @returns {Promise<Object|null>} Invitation object or null
   */
  async getInvitationByToken(token) {
    return await externalInvitationService.getInvitationByToken(token);
  },

  /**
   * Get invitations for a specific sales rep (from Supabase)
   * @param {string} salesRepId - Sales rep ID
   * @returns {Promise<Array>} List of invitations
   */
  async getInvitationsBySalesRep(salesRepId) {
    return await externalInvitationService.getInvitationsBySalesRep(salesRepId);
  },

  /**
   * Update invitation status (in Supabase)
   * @param {string} token - Invitation token
   * @param {string} status - New status (confirmed, declined, expired)
   * @param {Object} responseData - Additional response data
   * @returns {Promise<Object|null>} Updated invitation
   */
  async updateInvitationStatus(token, status, responseData = {}) {
    return await externalInvitationService.updateInvitationStatus(token, status, responseData);
  },
  
  /**
   * Check if invitation is expired
   * @param {Object} invitation - Invitation object
   * @returns {boolean} Whether invitation is expired
   */
  isInvitationExpired(invitation) {
    return externalInvitationService.isInvitationExpired(invitation);
  },
  
  /**
   * Generate professional email content for invitation
   * @param {Object} invitation - Invitation object
   * @returns {Object} Email content object
   */
  generateInvitationEmail(invitation) {
    const confirmationLink = this.generateConfirmationLink(invitation.token);
    const declineLink = this.generateDeclineLink(invitation.token);
    const meetingDateFormatted = new Date(invitation.meetingDate).toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    const subject = `Meeting Request from ${invitation.salesRepName} at ${invitation.salesRepCompany}`;
    
    const htmlBody = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Meeting Request - RepConnect</title>
        <style>
          .email-container { max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; }
          .header { background-color: #f8f9fa; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
          .content { background-color: white; padding: 30px; border: 1px solid #e9ecef; }
          .meeting-details { background-color: #f1f3f4; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .button { display: inline-block; padding: 12px 24px; margin: 10px 5px; text-decoration: none; border-radius: 6px; font-weight: bold; text-align: center; }
          .confirm-btn { background-color: #28a745; color: white; }
          .decline-btn { background-color: #dc3545; color: white; }
          .footer { background-color: #f8f9fa; padding: 20px; text-align: center; border-radius: 0 0 8px 8px; font-size: 12px; color: #6c757d; }
        </style>
      </head>
      <body>
        <div class="email-container">
          <div class="header">
            <h2 style="color: #495057; margin: 0;">Meeting Request</h2>
            <p style="color: #6c757d; margin: 5px 0 0 0;">RepConnect</p>
          </div>
          
          <div class="content">
            <p>Dear Dr. ${invitation.providerName.replace('Dr. ', '')},</p>
            
            <p>I hope this message finds you well. <strong>${invitation.salesRepName}</strong> from <strong>${invitation.salesRepCompany}</strong> would like to schedule a lunch meeting with you to discuss topics relevant to your practice.</p>
            
            <div class="meeting-details">
              <h3 style="margin-top: 0; color: #495057;">Proposed Meeting Details</h3>
              <ul style="list-style: none; padding: 0;">
                <li><strong>Date:</strong> ${meetingDateFormatted}</li>
                <li><strong>Time:</strong> ${invitation.meetingTime}</li>
                <li><strong>Location:</strong> ${invitation.restaurant}</li>
                ${invitation.restaurantAddress ? `<li><strong>Address:</strong> ${invitation.restaurantAddress}</li>` : ''}
                <li><strong>Representative:</strong> ${invitation.salesRepName}</li>
                <li><strong>Company:</strong> ${invitation.salesRepCompany}</li>
                ${invitation.salesRepPhone ? `<li><strong>Rep Phone:</strong> ${invitation.salesRepPhone}</li>` : ''}
              </ul>
              ${invitation.notes ? `<p><strong>Additional Notes:</strong><br>${invitation.notes}</p>` : ''}
            </div>
            
            <p>You can confirm or decline this meeting request using the links below:</p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${confirmationLink}" class="button confirm-btn">Confirm Meeting</a>
              <a href="${declineLink}" class="button decline-btn">Decline Meeting</a>
            </div>
            
            <p>If you have any questions or need to discuss alternative arrangements, please feel free to contact ${invitation.salesRepName} directly at <a href="mailto:${invitation.salesRepEmail}">${invitation.salesRepEmail}</a>.</p>
            
            <hr style="border: 1px solid #e9ecef; margin: 30px 0;">
            
            <div style="background-color: #e7f3ff; padding: 15px; border-radius: 6px; border-left: 4px solid #007bff;">
              <h4 style="margin: 0 0 10px 0; color: #0056b3;">Make Future Scheduling Even Easier</h4>
              <p style="margin: 0; font-size: 14px;">To streamline your interactions with pharmaceutical representatives and manage your lunch meetings more efficiently, consider downloading the <strong>RepConnect app</strong> - it's always free for healthcare providers.</p>
              <p style="margin: 10px 0 0 0; font-size: 14px;"><strong>Benefits for Healthcare Providers:</strong></p>
              <ul style="margin: 5px 0; font-size: 14px;">
                <li>Set your availability preferences once</li>
                <li>Manage dining preferences and dietary restrictions</li>
                <li>Track meeting history and representative contacts</li>
                <li>Reduce scheduling phone calls and emails</li>
                <li>Maintain compliance documentation automatically</li>
              </ul>
            </div>
            
            <p style="margin-top: 20px;"><strong>This invitation expires in 7 days.</strong> Please respond at your earliest convenience.</p>
          </div>
          
          <div class="footer">
            <p>This meeting request was sent through RepConnect, a healthcare meeting coordination platform.</p>
            <p>If you believe you received this email in error, please disregard this message.</p>
            <p>RepConnect - Streamlining Healthcare Professional Interactions</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    const textBody = `
      Dear Dr. ${invitation.providerName.replace('Dr. ', '')},

      ${invitation.salesRepName} from ${invitation.salesRepCompany} wants to schedule a lunch with you on ${meetingDateFormatted} at ${invitation.meetingTime}.

      Meeting Details:
      - Date: ${meetingDateFormatted}
      - Time: ${invitation.meetingTime}  
      - Location: ${invitation.restaurant}
      ${invitation.restaurantAddress ? `- Address: ${invitation.restaurantAddress}` : ''}
      - Representative: ${invitation.salesRepName}
      - Company: ${invitation.salesRepCompany}
      ${invitation.salesRepPhone ? `- Rep Phone: ${invitation.salesRepPhone}` : ''}

      ${invitation.notes ? `Additional Notes: ${invitation.notes}` : ''}

      You can confirm the lunch here: ${confirmationLink}
      You can decline here: ${declineLink}

      To make this process easier in the future for all of your representative meetings, download RepConnect app - it's always free for healthcare providers. It's a great way to manage your interactions with your sales representatives and book representative lunches with minimal interaction or disruption of your everyday workflows.

      This invitation expires in 7 days. For questions, contact ${invitation.salesRepName} at ${invitation.salesRepEmail}.

      Best regards,
      RepConnect
    `;
    
    return {
      subject,
      htmlBody,
      textBody,
      confirmationLink,
      declineLink
    };
  },
  
  /**
   * Generate confirmation link for external providers
   * @param {string} token - Invitation token
   * @returns {string} Confirmation URL
   */
  generateConfirmationLink(token) {
    // In a real application, this would be your actual domain
    const baseUrl = window.location.origin;
    return `${baseUrl}/meeting-confirmation?token=${token}&action=confirm`;
  },
  
  /**
   * Generate decline link for external providers  
   * @param {string} token - Invitation token
   * @returns {string} Decline URL
   */
  generateDeclineLink(token) {
    // In a real application, this would be your actual domain
    const baseUrl = window.location.origin;
    return `${baseUrl}/meeting-confirmation?token=${token}&action=decline`;
  },
  
  /**
   * Handle external provider response (confirm/decline)
   * @param {string} token - Invitation token
   * @param {string} action - 'confirm' or 'decline'
   * @param {Object} additionalData - Additional response data
   * @returns {Object} Response result
   */
  handleProviderResponse(token, action, additionalData = {}) {
    try {
      const invitation = this.getInvitationByToken(token);
      
      if (!invitation) {
        return { success: false, error: 'Invalid invitation token' };
      }
      
      if (this.isInvitationExpired(invitation)) {
        return { success: false, error: 'Invitation has expired' };
      }
      
      if (invitation.status !== 'pending') {
        return { success: false, error: 'Invitation has already been responded to' };
      }
      
      // Update invitation status
      const updatedInvitation = this.updateInvitationStatus(token, action === 'confirm' ? 'confirmed' : 'declined', {
        responseMethod: 'email_link',
        ...additionalData
      });
      
      if (action === 'confirm') {
        // Create meeting in the system
        const meetingResult = this.createMeetingFromInvitation(updatedInvitation);
        if (meetingResult.success) {
          this.updateInvitationStatus(token, 'confirmed', {
            meetingCreated: true,
            meetingId: meetingResult.meetingId
          });
        }
      }
      
      return { 
        success: true, 
        invitation: updatedInvitation,
        action: action
      };
      
    } catch (error) {
      logger.error('Error handling provider response', { error: error.message });
      return { success: false, error: 'Failed to process response' };
    }
  },
  
  /**
   * Create meeting from confirmed external invitation
   * @param {Object} invitation - Confirmed invitation
   * @returns {Object} Meeting creation result
   */
  createMeetingFromInvitation(invitation) {
    try {
      // Import MeetingStorage here to avoid circular dependencies
      const { MeetingStorage } = require('./meetingStorage');
      
      const meetingData = {
        customerId: `ext_${invitation.providerId}`, // External provider ID
        customer: invitation.providerName,
        customerEmail: invitation.providerEmail,
        customerPhone: 'Contact via email', // External providers don't have phone initially
        salesRepId: invitation.salesRepId,
        salesRepName: invitation.salesRepName,
        salesRepEmail: invitation.salesRepEmail,
        date: invitation.meetingDate,
        time: invitation.meetingTime,
        restaurant: invitation.restaurant,
        restaurantAddress: invitation.restaurantAddress,
        orderItems: [], // Will be populated later
        orderTotal: '0.00',
        notes: invitation.notes || 'Meeting confirmed via external invitation',
        status: 'confirmed', // External confirmations are automatically confirmed
        duration: 60,
        createdBy: 'external_invitation',
        externalInvitation: true,
        externalInvitationToken: invitation.token,
        // External provider details
        providerNPI: invitation.providerNPI,
        providerSpecialty: invitation.providerSpecialty,
        providerHospital: invitation.providerHospital
      };
      
      const newMeeting = MeetingStorage.createMeetingRequest(meetingData);
      
      if (newMeeting) {
        return { success: true, meetingId: newMeeting.id };
      } else {
        return { success: false, error: 'Failed to create meeting' };
      }
      
    } catch (error) {
      logger.error('Error creating meeting from invitation', { error: error.message });
      return { success: false, error: 'Failed to create meeting' };
    }
  },
  
  /**
   * Get pending invitations that need follow-up
   * @returns {Array} List of invitations needing attention
   */
  getPendingInvitations() {
    try {
      const invitations = this.getAllInvitations();
      const now = new Date();
      
      return invitations.filter(invitation => {
        if (invitation.status !== 'pending') return false;
        
        // Check if expired
        if (this.isInvitationExpired(invitation)) {
          // Auto-expire old pending invitations
          this.updateInvitationStatus(invitation.token, 'expired');
          return false;
        }
        
        return true;
      });
    } catch (error) {
      logger.error('Error getting pending invitations', { error: error.message });
      return [];
    }
  },
  
  /**
   * Get invitation statistics for a sales rep
   * @param {string} salesRepId - Sales rep ID
   * @returns {Object} Statistics object
   */
  getInvitationStats(salesRepId) {
    try {
      const invitations = this.getInvitationsBySalesRep(salesRepId);
      
      return {
        total: invitations.length,
        pending: invitations.filter(inv => inv.status === 'pending' && !this.isInvitationExpired(inv)).length,
        confirmed: invitations.filter(inv => inv.status === 'confirmed').length,
        declined: invitations.filter(inv => inv.status === 'declined').length,
        expired: invitations.filter(inv => inv.status === 'expired' || this.isInvitationExpired(inv)).length,
        conversionRate: invitations.length > 0 ? 
          ((invitations.filter(inv => inv.status === 'confirmed').length / invitations.length) * 100).toFixed(1) : 0
      };
    } catch (error) {
      logger.error('Error calculating invitation stats', { error: error.message });
      return { total: 0, pending: 0, confirmed: 0, declined: 0, expired: 0, conversionRate: 0 };
    }
  },
  
  /**
   * Export invitation data for reporting
   * @param {string} salesRepId - Sales rep ID (optional)
   * @returns {Array} CSV-compatible data
   */
  exportInvitationData(salesRepId = null) {
    try {
      const invitations = salesRepId ? 
        this.getInvitationsBySalesRep(salesRepId) : 
        this.getAllInvitations();
      
      return invitations.map(inv => ({
        'Invitation ID': inv.id,
        'Provider Name': inv.providerName,
        'Provider Email': inv.providerEmail,
        'Provider NPI': inv.providerNPI,
        'Provider Specialty': inv.providerSpecialty,
        'Sales Rep': inv.salesRepName,
        'Sales Rep Email': inv.salesRepEmail,
        'Meeting Date': inv.meetingDate,
        'Meeting Time': inv.meetingTime,
        'Restaurant': inv.restaurant,
        'Status': inv.status,
        'Created At': inv.createdAt,
        'Responded At': inv.respondedAt || 'Not responded',
        'Response Method': inv.responseMethod || 'None',
        'Expires At': inv.expiresAt
      }));
    } catch (error) {
      logger.error('Error exporting invitation data', { error: error.message });
      return [];
    }
  }
};

export default ExternalProviderInvitation;